import { createContext, useContext, useMemo } from 'react'
import { TelemetryDataContext } from './TelemetryDataContext'
import { computeStudentAnalytics } from '../components/computeSessionStats'
import { autoSegmentEvents } from '../../workspaceReplay2/utils/autoSegmentation'

export const StudentAnalyticsContext = createContext()

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build conversation array from raw telemetry events.
 * Matches the logic previously in buildStudentDetailData.
 */
function buildConversation(events) {
  return events
    .filter(e => e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE')
    .map(e => ({
      role: e.type === 'CHAT_QUERY' ? 'user' : 'assistant',
      content: e.payload?.text || e.payload?.message || e.payload?.content || '',
      timestamp: e.timestamp,
      intent: e.payload?.intent || null,
    }))
    .sort((a, b) => a.timestamp - b.timestamp)
}

/**
 * Build a short behavioral summary string for table display.
 */
function buildSummary(analytics, events) {
  const parts = []
  const duration = analytics.replayDuration ? Math.round(analytics.replayDuration / 60000) : 0
  parts.push(`${duration}m session.`)

  const testStats = analytics.sessionStats?.full?.testResults
  if (testStats?.lastTotalTests != null) {
    if (testStats.lastPassedCount === testStats.lastTotalTests) {
      parts.push(`Passed all ${testStats.lastTotalTests} tests.`)
    } else {
      parts.push(`Passed ${testStats.lastPassedCount || 0}/${testStats.lastTotalTests} tests.`)
    }
  }

  const queryCount = analytics.userMessages?.length || 0
  if (queryCount > 0) {
    parts.push(`Used AI ${queryCount} time${queryCount !== 1 ? 's' : ''}.`)
    if (analytics.firstQueryTime != null) {
      parts.push(`First query at ${Math.round(analytics.firstQueryTime / 60)}m.`)
    }
  } else {
    parts.push('No AI usage.')
  }

  const errors = events.filter(e => e.type === 'TERMINAL_ERROR').length
  if (errors > 0) parts.push(`${errors} error${errors !== 1 ? 's' : ''}.`)

  return parts.join(' ')
}

/**
 * Compute aggregate statistics across all students.
 */
function computeClassAggregates(analyticsMap) {
  const entries = Object.values(analyticsMap)
  if (entries.length === 0) return emptyAggregates()

  // ── Collect per-student scalars ──
  const firstQueryTimes = []
  const timeToFirstRuns = []
  const timesToCompletion = []
  const runsBeforeAllPasses = []
  const codeAtFirstQueries = []
  const queryCounts = []
  const netCodeGrowths = []
  let completedCount = 0
  let aiUserCount = 0

  entries.forEach(({ analytics }) => {
    // Time to first query
    if (analytics.firstQueryTime != null) {
      firstQueryTimes.push(analytics.firstQueryTime)
      aiUserCount++
    }

    // Time to first run
    if (analytics.timeToFirstRun != null) timeToFirstRuns.push(analytics.timeToFirstRun)

    // Completion
    if (analytics.timeToCompletion != null) {
      timesToCompletion.push(analytics.timeToCompletion)
      completedCount++
    }

    // Runs before all-pass
    if (analytics.runsBeforeAllPass != null) runsBeforeAllPasses.push(analytics.runsBeforeAllPass)

    // Code at first query
    if (analytics.codeAtFirstQuery != null) codeAtFirstQueries.push(analytics.codeAtFirstQuery)

    // Query count
    queryCounts.push(analytics.userMessages?.length || 0)

    // Net code growth
    const ncg = analytics.sessionStats?.full?.codeMetrics?.netCodeGrowth
    if (ncg != null) netCodeGrowths.push(ncg)
  })

  const median = (arr) => {
    if (arr.length === 0) return null
    const sorted = [...arr].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
  }

  const avg = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null

  return {
    totalStudents: entries.length,
    completedCount,
    aiUserCount,

    firstQueryTime: { avg: avg(firstQueryTimes), median: median(firstQueryTimes), values: firstQueryTimes },
    timeToFirstRun: { avg: avg(timeToFirstRuns), median: median(timeToFirstRuns), values: timeToFirstRuns },
    timeToCompletion: { avg: avg(timesToCompletion), median: median(timesToCompletion), values: timesToCompletion },
    runsBeforeAllPass: { avg: avg(runsBeforeAllPasses), median: median(runsBeforeAllPasses), values: runsBeforeAllPasses },
    codeAtFirstQuery: { avg: avg(codeAtFirstQueries), median: median(codeAtFirstQueries), values: codeAtFirstQueries },
    queryCount: { avg: avg(queryCounts), median: median(queryCounts), values: queryCounts },
    netCodeGrowth: { avg: avg(netCodeGrowths), median: median(netCodeGrowths), values: netCodeGrowths },
  }
}

function emptyAggregates() {
  const empty = { avg: null, median: null, values: [] }
  return {
    totalStudents: 0, completedCount: 0, aiUserCount: 0,
    firstQueryTime: empty, timeToFirstRun: empty, timeToCompletion: empty,
    runsBeforeAllPass: empty, codeAtFirstQuery: empty,
    queryCount: empty, netCodeGrowth: empty,
  }
}

// ─── Per-student analytics for a whole session ───────────────────────────────

// What the provider below computes from one session's telemetry, as a plain function so a
// page can compute it for sessions it has no provider for (the experiment page's
// all-conditions export).
export const computeAnalyticsById = (eventsByUser) => {
  const userIds = Object.keys(eventsByUser || {})
  if (userIds.length === 0) {
    return { analyticsById: {}, classAggregates: emptyAggregates() }
  }

  const analyticsMap = {}

  userIds.forEach(userId => {
    const rawEvents = eventsByUser[userId]
    if (!rawEvents || rawEvents.length === 0) return

    // Skip sessions under 15 seconds or with only SESSION_START events
    const first = rawEvents[0]?.timestamp
    const last = rawEvents[rawEvents.length - 1]?.timestamp
    if (!first || !last || (last - first) < 15000) return
    if (!rawEvents.some(e => e.type !== 'SESSION_START')) return

    const conversation = buildConversation(rawEvents)
    const analytics = computeStudentAnalytics({ rawEvents, conversation, autoSegmentEvents })
    const summary = buildSummary(analytics, rawEvents)

    analyticsMap[userId] = { analytics, conversation, summary, rawEvents }
  })

  const classAggregates = computeClassAggregates(analyticsMap)

  return { analyticsById: analyticsMap, classAggregates }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const StudentAnalyticsProvider = ({ children }) => {
  const { eventsByUser } = useContext(TelemetryDataContext)

  const { analyticsById, classAggregates } = useMemo(() => computeAnalyticsById(eventsByUser), [eventsByUser])

  return (
    <StudentAnalyticsContext.Provider value={{
      analyticsById,
      classAggregates,
    }}>
      {children}
    </StudentAnalyticsContext.Provider>
  )
}